import { createSlice } from '@reduxjs/toolkit';
import {
  buildDrivingRouteGroupName,
  buildModePointDraft,
  normalizeDrivingRouteGroupKey,
} from '@utils/mode_tool/modeRoute';
import { resolveMediaKind } from '@utils/media/mediaUrl';
import {
  buildContextKey,
  getModePoints,
  normalizeDrivingMainWithoutTouchingAlternates,
  normalizeDrivingPointsSequentially,
  normalizeRouteKey,
} from '@utils/mode_tool/draftRouteHelpers';

// Normalizes localized objects (`{ en: { value,id }, es: ... }`) to array format used by mode drafts.
function normalizeLocalizedArray(value) {
  if (!value || typeof value !== 'object') return [];
  return Object.entries(value).reduce((acc, [iso, entry]) => {
    if (!iso) return acc;
    if (entry && typeof entry === 'object') {
      const normalizedValue = String(entry.value || '');
      acc.push({
        [iso]: normalizedValue,
        id: normalizedValue.trim() ? entry.id || '' : '',
      });
      return acc;
    }
    acc.push({ [iso]: String(entry || ''), id: '' });
    return acc;
  }, []);
}


function normalizeLocalizedMap(value) {
  if (!value || typeof value !== 'object') return {};
  if (Array.isArray(value)) {
    return value.reduce((acc, item) => {
      if (!item || typeof item !== 'object') return acc;
      const entry = Object.entries(item).find(([key]) => key !== 'id');
      if (!entry) return acc;
      const [iso, localizedValue] = entry;
      const normalizedIso = String(iso || '').toLowerCase().trim();
      if (!normalizedIso) return acc;
      acc[normalizedIso] = {
        id: String(item?.id || ''),
        value: String(localizedValue || ''),
      };
      return acc;
    }, {});
  }
  return Object.entries(value).reduce((acc, [iso, entry]) => {
    const normalizedIso = String(iso || '').toLowerCase().trim();
    if (!normalizedIso) return acc;
    acc[normalizedIso] =
      entry && typeof entry === 'object'
        ? {
            id: String(entry?.id || ''),
            value: String(entry?.value || ''),
          }
        : String(entry || '');
    return acc;
  }, {});
}

// AI audio descriptions follow the same per-language draft lifecycle as the
// normal description. Keep their full content while the mode panel rebuilds
// its canonical point representation.
function normalizeLocalizedAiAudioDescription(value) {
  if (!value || typeof value !== 'object') return {};
  const source = Array.isArray(value)
    ? value.reduce((acc, item) => {
        if (!item || typeof item !== 'object') return acc;
        const entry = Object.entries(item).find(([key]) => key !== 'id');
        if (!entry) return acc;
        const [iso, content] = entry;
        acc[iso] = { ...(content || {}), id: String(item?.id || content?.id || '') };
        return acc;
      }, {})
    : value;
  return Object.entries(source).reduce((acc, [iso, content]) => {
    const language = String(iso || '').toLowerCase().trim();
    if (!language || !content || typeof content !== 'object') return acc;
    acc[language] = { ...content, id: String(content?.id || '') };
    return acc;
  }, {});
}

// Builds the canonical point payload expected by mode-tool save/update flows.
function buildOrganizedPoint(point = {}, context = {}) {
  const multimedia = Array.isArray(point?.multimedia) ? point.multimedia : [];
  const pointOrder = Number(point?.order) || 0;
  const normalizedDrivingRouteGroup = normalizeDrivingRouteGroupKey(
    point?.routeGroup ?? point?.route_group ?? '',
    point?.routeGroupName ?? point?.route_group_name ?? '',
    pointOrder,
  );
  const preservedAlternateGroupName =
    normalizedDrivingRouteGroup !== 'main'
      ? String(point?.routeGroupName ?? point?.route_group_name ?? '').trim()
      : '';
  const localizedAudioMap = multimedia.reduce((acc, item) => {
    if (resolveMediaKind(item) !== 'audio') return acc;
    const language = String(item?.language || item?.languageIso || '').toLowerCase().trim();
    if (!language) return acc;
    acc.set(language, {
      url: item?.url || '',
      id: item?.sourceId || item?.id || '',
      language,
      ...(Number(item?.durationSeconds ?? item?.duration_seconds) > 0
        ? { durationSeconds: Number(item?.durationSeconds ?? item?.duration_seconds) }
        : {}),
    });
    return acc;
  }, new Map());
  (Array.isArray(point?.audios) ? point.audios : []).forEach((item) => {
    const language = String(item?.language || item?.languageIso || '').toLowerCase().trim();
    if (!language) return;
    localizedAudioMap.set(language, {
      url: item?.url || '',
      id: item?.id || '',
      language,
      ...(Number(item?.durationSeconds ?? item?.duration_seconds) > 0
        ? { durationSeconds: Number(item?.durationSeconds ?? item?.duration_seconds) }
        : {}),
    });
  });
  return {
    id: String(point?.id || ''),
    isPersisted: Boolean(point?.isPersisted),
    internalId: String(point?.internalId || ''),
    serverId: String(point?.serverId || ''),
    uuid: String(point?.uuid || ''),
    number_point: Number(point?.order) || 0,
    name: normalizeLocalizedArray(point?.name || {}),
    description: normalizeLocalizedArray(point?.description || {}),
    aiAudioDescription: normalizeLocalizedAiAudioDescription(
      point?.aiAudioDescription ?? point?.ai_audio_description,
    ),
    more_info: normalizeLocalizedArray(point?.more_info || {}),
    notes: String(point?.notes ?? point?.note ?? ''),
    ai_brief: String(point?.ai_brief ?? point?.aiBrief ?? ''),
    music: point?.music && typeof point.music === 'object' ? point.music : null,
    mixed_audio: point?.mixed_audio && typeof point.mixed_audio === 'object' ? point.mixed_audio : null,
    radius_meters: Number(point?.radiusMeters ?? 0) || 0,
    turn_case: Number(point?.turnCase ?? 0) || 0,
    pointType: String(point?.pointType ?? point?.point_type ?? ''),
    route_group: normalizedDrivingRouteGroup,
    route_group_name:
      preservedAlternateGroupName ||
      buildDrivingRouteGroupName(pointOrder, normalizedDrivingRouteGroup),
    labelCode:
      normalizedDrivingRouteGroup !== 'main'
        ? String(point?.labelCode ?? point?.label_code ?? preservedAlternateGroupName)
        : '',
    route_group_parent_point_id: '',
    isGeneratedDirection: Boolean(
      point?.isGeneratedDirection ?? point?.is_generated_direction ?? false,
    ),
    latitude: String(point?.latitude ?? ''),
    longitude: String(point?.longitude ?? ''),
    images: multimedia
      .filter((item) => resolveMediaKind(item) === 'image')
      .map((item, index) => ({
        url: item?.url || '',
        id: item?.sourceId || item?.id || '',
        updatedAt:
          String(item?.updatedAt || '').trim() ||
          `${new Date().toISOString().slice(0, 10)}T${String(index).padStart(2, '0')}:00:00.000Z`,
      })),
    audios: Array.from(localizedAudioMap.values())
  };
}

// Keeps `selectedModeDraft.points` in sync with the currently selected (ownerId, mode, routeTypeId) context.
function syncOrganizedPointsForCurrentContext(state, startPointId, mode, routeKey = "default") {
  const selected = state.selectedModeContext || {};
  const selectedContextKey = buildContextKey(selected);
  if (!selectedContextKey) {
    state.selectedModeDraft.points = [];
    return;
  }
  if (String(selected.ownerId || '') !== String(startPointId || '')) return;
  if (String(selected.mode || '') !== String(mode || '')) return;
  const selectedRouteKey = normalizeRouteKey(selected.mode, state.selectedWalkingRouteKey);
  const nextRouteKey = normalizeRouteKey(mode, routeKey);
  if (selectedRouteKey !== nextRouteKey) return;
  const source = getModePoints(state, startPointId, mode, nextRouteKey);
  state.selectedModeDraft.points = source.map((point) => buildOrganizedPoint(point, selected));
}

const modeToolDraftSlice = createSlice({
  name: 'modeToolDraft',
  initialState: {
    selectedMode: '',
    // Tracks the selected `tour_types[].id` for the active mode.
    selectedTourTypeId: '',
    // UI-only selected point id (accordion/map focus), not the tour type id.
    selectedModePointId: '',
    modeOptions: [],
    modePointsByStartPoint: {},
    selectedWalkingRouteKey: '',
    walkingRouteKeysByOwner: {},
    walkingRouteMetaByOwner: {},
    selectedModeContext: {
      ownerId: '',
      routeTypeId: '',
      mode: '',
      sourceFrom: '',
      sourceName: ''
    },
    // Single structured draft to avoid duplicated sources of truth.
    selectedModeDraft: {
      context: {
        ownerId: '',
        routeTypeId: '',
        mode: '',
        sourceFrom: '',
        sourceName: ''
      },
      points: []
    }
  },
  reducers: {
    // Clears full Mode Tool draft state. Used on explicit close or start-point context switch.
    resetModeToolDraft(state) {
      state.selectedMode = '';
      state.selectedTourTypeId = '';
      state.selectedModePointId = '';
      state.modeOptions = [];
      state.modePointsByStartPoint = {};
      state.selectedWalkingRouteKey = '';
      state.walkingRouteKeysByOwner = {};
      state.walkingRouteMetaByOwner = {};
      state.selectedModeContext = {
        ownerId: '',
        routeTypeId: '',
        mode: '',
        sourceFrom: '',
        sourceName: ''
      };
      state.selectedModeDraft = {
        context: {
          ownerId: '',
          routeTypeId: '',
          mode: '',
          sourceFrom: '',
          sourceName: ''
        },
        points: []
      };
    },
    setModeOptions(state, action) {
      state.modeOptions = Array.isArray(action.payload) ? action.payload : [];
    },
    setSelectedWalkingRouteKey(state, action) {
      state.selectedWalkingRouteKey = String(action.payload || '');
      const selected = state.selectedModeContext || {};
      if (!selected?.ownerId || !selected?.mode) return;
      syncOrganizedPointsForCurrentContext(
        state,
        selected.ownerId,
        selected.mode,
        state.selectedWalkingRouteKey
      );
    },
    addWalkingRoute(state, action) {
      const ownerId = String(action.payload?.ownerId || '');
      if (!ownerId) return;
      const current = Array.isArray(state.walkingRouteKeysByOwner?.[ownerId])
        ? state.walkingRouteKeysByOwner[ownerId]
        : [];
      const existingNums = current
        .map((key) => Number(String(key).replace('new-', '')))
        .filter((num) => Number.isFinite(num) && num > 0);
      const nextNum = existingNums.length ? Math.max(...existingNums) + 1 : 1;
      const nextKey = `new-${nextNum}`;
      state.walkingRouteKeysByOwner[ownerId] = [...current, nextKey];
      if (!state.walkingRouteMetaByOwner[ownerId]) state.walkingRouteMetaByOwner[ownerId] = {};
      state.walkingRouteMetaByOwner[ownerId][nextKey] = {
        name: {},
      };
      if (!state.modePointsByStartPoint[ownerId]) state.modePointsByStartPoint[ownerId] = {};
      if (!state.modePointsByStartPoint[ownerId].walking) state.modePointsByStartPoint[ownerId].walking = {};
      state.modePointsByStartPoint[ownerId].walking[nextKey] = [];
      state.selectedWalkingRouteKey = nextKey;
      syncOrganizedPointsForCurrentContext(state, ownerId, 'walking', nextKey);
    },
    removeWalkingRoute(state, action) {
      const ownerId = String(action.payload?.ownerId || '');
      const routeKey = String(action.payload?.routeKey || '');
      if (!ownerId || !routeKey) return;
      const keys = Array.isArray(state.walkingRouteKeysByOwner?.[ownerId])
        ? state.walkingRouteKeysByOwner[ownerId]
        : [];
      const nextKeys = keys.filter((key) => key !== routeKey);
      state.walkingRouteKeysByOwner[ownerId] = nextKeys;
      if (state.modePointsByStartPoint?.[ownerId]?.walking) {
        delete state.modePointsByStartPoint[ownerId].walking[routeKey];
      }
      if (state.walkingRouteMetaByOwner?.[ownerId]) {
        delete state.walkingRouteMetaByOwner[ownerId][routeKey];
      }
      if (String(state.selectedWalkingRouteKey || '') === routeKey) {
        state.selectedWalkingRouteKey = nextKeys[0] || '';
      }
      syncOrganizedPointsForCurrentContext(state, ownerId, 'walking', state.selectedWalkingRouteKey || '');
    },
    setWalkingRoutesSnapshot(state, action) {
      const ownerId = String(action.payload?.ownerId || '');
      const routes = Array.isArray(action.payload?.routes) ? action.payload.routes : [];
      if (!ownerId) return;
      if (!state.modePointsByStartPoint[ownerId]) state.modePointsByStartPoint[ownerId] = {};
      state.modePointsByStartPoint[ownerId].walking = {};
      state.walkingRouteMetaByOwner[ownerId] = {};
      const nextKeys = [];
      routes.forEach((route) => {
        const routeKey = String(route?.routeKey || '');
        if (!routeKey) return;
        nextKeys.push(routeKey);
        state.modePointsByStartPoint[ownerId].walking[routeKey] = Array.isArray(route?.points) ? route.points : [];
        state.walkingRouteMetaByOwner[ownerId][routeKey] = {
          name: normalizeLocalizedMap(
            route?.name || route?.routeName || route?.route_name || {},
          ),
        };
      });
      state.walkingRouteKeysByOwner[ownerId] = nextKeys;
      if (!nextKeys.includes(String(state.selectedWalkingRouteKey || ''))) {
        state.selectedWalkingRouteKey = nextKeys[0] || '';
      }
      syncOrganizedPointsForCurrentContext(state, ownerId, 'walking', state.selectedWalkingRouteKey || '');
    },
    setWalkingRouteName(state, action) {
      const ownerId = String(action.payload?.ownerId || '');
      const routeKey = String(action.payload?.routeKey || '');
      const iso = String(action.payload?.iso || '').toLowerCase().trim();
      const value = action.payload?.value ?? '';
      if (!ownerId || !routeKey || !iso) return;
      if (!state.walkingRouteMetaByOwner[ownerId]) state.walkingRouteMetaByOwner[ownerId] = {};
      const currentMeta = state.walkingRouteMetaByOwner[ownerId][routeKey] || {};
      const currentName = currentMeta.name || {};
      const currentValue = currentName[iso];
      state.walkingRouteMetaByOwner[ownerId][routeKey] = {
        ...currentMeta,
        name: {
          ...currentName,
          [iso]:
            currentValue && typeof currentValue === 'object'
              ? { ...currentValue, value }
              : value,
        },
      };
    },
    setSelectedMode(state, action) {
      state.selectedMode = String(action.payload || '');
    },
    setSelectedModePointId(state, action) {
      state.selectedModePointId = String(action.payload || '');
    },
    setSelectedModeContext(state, action) {
      const payload = action.payload || {};
      const mode = String(payload.mode || '');
      const ownerId = String(payload.ownerId || '');
      state.selectedModeContext = {
        ownerId,
        routeTypeId: String(payload.routeTypeId || ''),
        mode,
        sourceFrom: String(payload.sourceFrom || ''),
        sourceName: String(payload.sourceName || '')
      };
      if (mode === 'walking' && ownerId) {
        const existing = Array.isArray(state.walkingRouteKeysByOwner?.[ownerId])
          ? state.walkingRouteKeysByOwner[ownerId]
          : [];
        state.walkingRouteKeysByOwner[ownerId] = existing;
        if (state.selectedWalkingRouteKey && !existing.includes(state.selectedWalkingRouteKey)) {
          state.selectedWalkingRouteKey = existing[0] || '';
        }
      }
      // Persist selected tour type id (`tour_types[].id`) explicitly.
      state.selectedTourTypeId = String(payload.routeTypeId || '');
      state.selectedModeDraft.context = { ...state.selectedModeContext };
      const contextKey = buildContextKey(state.selectedModeContext);
      if (!contextKey) {
        state.selectedModeDraft.points = [];
        return;
      }
      const routeKey = normalizeRouteKey(mode, state.selectedWalkingRouteKey);
      const source = getModePoints(state, state.selectedModeContext.ownerId, state.selectedModeContext.mode, routeKey);
      state.selectedModeDraft.points = source.map((point) => buildOrganizedPoint(point, state.selectedModeContext));
    },
    setModePointsForStartMode(state, action) {
      const { startPointId, mode, points, routeKey } = action.payload || {};
      if (!startPointId || !mode) return;
      if (!state.modePointsByStartPoint[startPointId]) state.modePointsByStartPoint[startPointId] = {};
      const normalizedRouteKey = normalizeRouteKey(mode, routeKey || state.selectedWalkingRouteKey);
      if (!state.modePointsByStartPoint[startPointId][mode] || typeof state.modePointsByStartPoint[startPointId][mode] !== 'object') {
        state.modePointsByStartPoint[startPointId][mode] = {};
      }
      state.modePointsByStartPoint[startPointId][mode][normalizedRouteKey] = Array.isArray(points) ? points : [];
      if (mode === 'walking') {
        const current = Array.isArray(state.walkingRouteKeysByOwner?.[startPointId])
          ? state.walkingRouteKeysByOwner[startPointId]
          : [];
        if (!current.includes(normalizedRouteKey)) {
          state.walkingRouteKeysByOwner[startPointId] = [...current, normalizedRouteKey];
        }
      }
      syncOrganizedPointsForCurrentContext(state, startPointId, mode, normalizedRouteKey);
    },
    syncModePointsFromMap(state, action) {
      // Syncs marker geometry back into redux while preserving metadata already entered per point.
      const { startPointId, mode, mapPoints, routeKey } = action.payload || {};
      if (!startPointId || !mode) return;
      if (!state.modePointsByStartPoint[startPointId]) state.modePointsByStartPoint[startPointId] = {};
      const normalizedRouteKey = normalizeRouteKey(mode, routeKey || state.selectedWalkingRouteKey);
      if (!state.modePointsByStartPoint[startPointId][mode] || typeof state.modePointsByStartPoint[startPointId][mode] !== 'object') {
        state.modePointsByStartPoint[startPointId][mode] = {};
      }
      const previousPoints = getModePoints(state, startPointId, mode, normalizedRouteKey);
      const isGeneratedDirectionInsertion =
        String(mode || '') === 'driving' &&
        (Array.isArray(mapPoints) ? mapPoints : []).some(
          (mapPoint) => Boolean(mapPoint?.isGeneratedDirection),
        );
      const activeMapPointIds = new Set(
        (Array.isArray(mapPoints) ? mapPoints : []).map((mapPoint, index) =>
          String(mapPoint?.id || `point-${index + 1}`),
        ),
      );
      const next = (Array.isArray(mapPoints) ? mapPoints : []).map((mapPoint, index) => {
        const mapId = mapPoint.id || `point-${index + 1}`;
        const existing = previousPoints.find((item) => item.id === mapId);
        const mapProvidesGeneratedDirection = Object.prototype.hasOwnProperty.call(
          mapPoint || {},
          'isGeneratedDirection',
        );
        const basePayload = {
          ...(mapPoint || {}),
          ...(existing || {}),
          id: mapId,
          order:
            isGeneratedDirectionInsertion && existing
              ? existing.order
              : mapPoint.order || index + 1,
          latitude: mapPoint.lat ?? mapPoint.latitude,
          longitude: mapPoint.lng ?? mapPoint.longitude,
          isGeneratedDirection: mapProvidesGeneratedDirection
            ? Boolean(mapPoint?.isGeneratedDirection)
            : Boolean(
                existing?.isGeneratedDirection ??
                  existing?.is_generated_direction ??
                  false,
              ),
        };
        if (!existing && String(mode || '') === 'driving') {
          const routeGroup = normalizeDrivingRouteGroupKey(
            basePayload?.routeGroup ?? basePayload?.route_group ?? '',
            basePayload?.routeGroupName ?? basePayload?.route_group_name ?? '',
            basePayload.order,
          );
          return {
            ...buildModePointDraft({
              ...basePayload,
              routeGroup,
              routeGroupName: buildDrivingRouteGroupName(
                basePayload.order,
                routeGroup,
              ),
              routeGroupParentPointId: '',
            }),
            __preserveExistingMainOrder: false,
          };
        }
        return {
          ...buildModePointDraft(basePayload),
          ...(isGeneratedDirectionInsertion
            ? { __preserveExistingMainOrder: Boolean(existing) }
            : {}),
        };
      });
      state.modePointsByStartPoint[startPointId][mode][normalizedRouteKey] =
        String(mode || '') === 'driving'
          ? isGeneratedDirectionInsertion
            ? normalizeDrivingMainWithoutTouchingAlternates(next)
            : normalizeDrivingPointsSequentially(next)
          : next;
      syncOrganizedPointsForCurrentContext(state, startPointId, mode, normalizedRouteKey);
    },
    updateModePointField(state, action) {
      // Updates simple scalar fields (name, description, radius, etc.) for one mode point.
      const { startPointId, mode, pointId, field, value, routeKey } = action.payload || {};
      if (!startPointId || !mode || !pointId || !field) return;
      const normalizedRouteKey = normalizeRouteKey(mode, routeKey || state.selectedWalkingRouteKey);
      const current = getModePoints(state, startPointId, mode, normalizedRouteKey);
      state.modePointsByStartPoint[startPointId][mode][normalizedRouteKey] = current.map((item) =>
        item.id === pointId
          ? { ...item, [field]: field === 'radiusMeters' ? Number(value) || 0 : value }
          : item
      );
      syncOrganizedPointsForCurrentContext(state, startPointId, mode, normalizedRouteKey);
    },
    patchModePoint(state, action) {
      // Patches popup payload while normalizing numeric fields used by backend contracts.
      const { startPointId, mode, pointId, payload, routeKey } = action.payload || {};
      if (!startPointId || !mode || !pointId) return;
      const normalizedRouteKey = normalizeRouteKey(mode, routeKey || state.selectedWalkingRouteKey);
      const current = getModePoints(state, startPointId, mode, normalizedRouteKey);
      state.modePointsByStartPoint[startPointId][mode][normalizedRouteKey] = current.map((item) =>
        item.id === pointId
          ? {
              ...item,
              ...(payload || {}),
              radiusMeters: Number(payload?.radiusMeters ?? item.radiusMeters ?? 0) || 0,
              turnCase: Number(payload?.turnCase ?? item.turnCase ?? 0) || 0
            }
          : item
      );
      syncOrganizedPointsForCurrentContext(state, startPointId, mode, normalizedRouteKey);
    },
    appendModePointMedia(state, action) {
      // Appends uploaded media references to a mode point.
      const { startPointId, mode, pointId, media, routeKey } = action.payload || {};
      if (!startPointId || !mode || !pointId || !Array.isArray(media) || !media.length) return;
      const normalizedRouteKey = normalizeRouteKey(mode, routeKey || state.selectedWalkingRouteKey);
      const current = getModePoints(state, startPointId, mode, normalizedRouteKey);
      state.modePointsByStartPoint[startPointId][mode][normalizedRouteKey] = current.map((item) =>
        item.id === pointId ? { ...item, multimedia: [...(item.multimedia || []), ...media] } : item
      );
      syncOrganizedPointsForCurrentContext(state, startPointId, mode, normalizedRouteKey);
    },
    removeModePointMedia(state, action) {
      // Removes one media item from a mode point.
      const { startPointId, mode, pointId, mediaId, routeKey } = action.payload || {};
      if (!startPointId || !mode || !pointId || !mediaId) return;
      const normalizedRouteKey = normalizeRouteKey(mode, routeKey || state.selectedWalkingRouteKey);
      const current = getModePoints(state, startPointId, mode, normalizedRouteKey);
      state.modePointsByStartPoint[startPointId][mode][normalizedRouteKey] = current.map((item) =>
        item.id === pointId
          ? { ...item, multimedia: (item.multimedia || []).filter((media) => media.id !== mediaId) }
          : item
      );
      syncOrganizedPointsForCurrentContext(state, startPointId, mode, normalizedRouteKey);
    },
    removeModePoint(state, action) {
      // Deletes one point from the currently active (startPoint, mode) collection.
      const { startPointId, mode, pointId, routeKey } = action.payload || {};
      if (!startPointId || !mode || !pointId) return;
      const normalizedRouteKey = normalizeRouteKey(mode, routeKey || state.selectedWalkingRouteKey);
      const current = getModePoints(state, startPointId, mode, normalizedRouteKey);
      const next = current
        .filter((item) => item.id !== pointId)
        .map((item, index) => ({
          ...item,
          order: index + 1
        }));
      state.modePointsByStartPoint[startPointId][mode][normalizedRouteKey] =
        String(mode || '') === 'driving'
          ? normalizeDrivingPointsSequentially(next)
          : next;
      syncOrganizedPointsForCurrentContext(state, startPointId, mode, normalizedRouteKey);
    }
  }
});

export const {
  resetModeToolDraft,
  setModeOptions,
  setSelectedWalkingRouteKey,
  addWalkingRoute,
  removeWalkingRoute,
  setWalkingRoutesSnapshot,
  setWalkingRouteName,
  setSelectedMode,
  setSelectedModePointId,
  setSelectedModeContext,
  setModePointsForStartMode,
  syncModePointsFromMap,
  updateModePointField,
  patchModePoint,
  appendModePointMedia,
  removeModePointMedia,
  removeModePoint
} = modeToolDraftSlice.actions;

export default modeToolDraftSlice.reducer;
